import React, { useRef, useEffect } from "react";
import { Box, Divider, Typography } from "@mui/material";
import * as echarts from "echarts";
import { ChartCardProps } from "../../type";
import { BaseCard } from "./BaseCard";
import { operationsDashboardStyles } from "../../styles";
import { colors } from "../../../../utils/colors";
import { formatValue } from "../../utils";

const axisTooltipBase = {
  trigger: "axis",
  axisPointer: { type: "shadow" },
  appendToBody: true,
  confine: false,
  extraCssText: "z-index: 10000;",
};

const shouldUseLogAxisForBarValues = (rawValues: number[]): boolean => {
  const positives = rawValues.filter((v) => v > 0);
  if (positives.length < 2) return false;
  const maxVal = Math.max(...positives);
  const minVal = Math.min(...positives);
  return maxVal / minVal >= 100;
};

type BarChartTooltipOptions = {
  tooltipValuePrefix?: string;
  tooltipValueLabel?: string;
  tooltipSecondaryValueKey?: string;
  tooltipSecondaryValueLabel?: string;
  tooltipSecondaryValuePrefix?: string;
};

type AxisTooltipRow = {
  axisValueLabel?: string;
  axisValue?: string;
  value?: unknown;
  color?: string;
  marker?: string;
  data?: Record<string, unknown>;
};

const formatAxisBarTooltip = (
  params: unknown,
  tooltipOpts: BarChartTooltipOptions | undefined,
  defaultSeriesColor: string,
): string => {
  const row = Array.isArray(params) ? (params[0] as AxisTooltipRow) : undefined;
  if (!row) return "";
  const valuePrefix = tooltipOpts?.tooltipValuePrefix ?? "";
  const valueLabel = tooltipOpts?.tooltipValueLabel;
  const secondaryValueKey = tooltipOpts?.tooltipSecondaryValueKey;
  const secondaryValueLabel = tooltipOpts?.tooltipSecondaryValueLabel;
  const secondaryValuePrefix = tooltipOpts?.tooltipSecondaryValuePrefix ?? "";
  const label = String(row.axisValueLabel ?? row.axisValue ?? "");
  const num = Number(row.value);
  const secondaryNum = Number(
    secondaryValueKey ? row.data?.[secondaryValueKey] : Number.NaN,
  );
  const hasPrimaryValue = !Number.isNaN(num);
  const hasSecondaryValue = !Number.isNaN(secondaryNum);
  const markerColor =
    typeof row.color === "string" && row.color.length > 0
      ? row.color
      : defaultSeriesColor;
  const markerHtml =
    typeof row.marker === "string" && row.marker.length > 0
      ? row.marker
      : `<span style="display:inline-block;width:8px;height:8px;border-radius:50%;background:${markerColor};"></span>`;
  if (!hasPrimaryValue && !hasSecondaryValue) return label;
  const primaryLabelText = valueLabel ? `${valueLabel}: ` : "";
  const secondaryLabelText = secondaryValueLabel
    ? `${secondaryValueLabel}: `
    : "";
  const primaryValueText = `${valuePrefix}${formatValue(num)}`;
  const secondaryValueText = `${secondaryValuePrefix}${formatValue(secondaryNum)}`;

  return `
    <div style="font-size:0.75rem;">${label}</div>
<div style="margin-top:8px;">
  ${
    hasPrimaryValue
      ? `
    <div style="display:flex; align-items:center; gap:4px; margin-left:8px;">
      ${markerHtml}
      <span>
        <span style="font-weight:400;">${primaryLabelText}</span>
        <span style="font-weight:700;">${primaryValueText}</span>
      </span>
    </div>`
      : ""
  }
  ${
    hasSecondaryValue
      ? `
    <div style="display:flex; align-items:center; gap:4px; margin-left:8px;">
      ${markerHtml}
      <span>
        <span style="font-weight:400;">${secondaryLabelText}</span>
        <span style="font-weight:700;">${secondaryValueText}</span>
      </span>
    </div>`
      : ""
  }
</div>
  `;
};

const createHorizontalBarOption = (
  data: any[],
  tooltipOpts?: BarChartTooltipOptions,
) => {
  const rawValues = data.map((item) => Number(item.value));
  const useLogAxis = shouldUseLogAxisForBarValues(rawValues);
  return {
    tooltip: {
      ...axisTooltipBase,
      textStyle: {
        color: colors.chart_text,
        fontSize: "0.55rem",
        fontWeight: 400,
      },
      formatter: (params: unknown) =>
        formatAxisBarTooltip(params, tooltipOpts, colors.chart_blue_light),
    },
    grid: {
      left: "18%",
      right: "10%",
      top: "10%",
      bottom: "25%",
      containLabel: false,
    },
    xAxis: {
      type: useLogAxis ? "log" : "value",
      ...(useLogAxis ? { logBase: 10 } : {}),
      min: useLogAxis ? undefined : 0,
      show: false,
      splitLine: {
        show: false,
      },
    },
    yAxis: {
      type: "category",
      data: data.map((item) => item.label),
      axisLine: {
        show: false,
      },
      axisTick: {
        show: false,
      },
      axisLabel: {
        fontSize: "0.75rem",
        color: colors.chart_text,
        fontWeight: 400,
        fontFamily: "Inter",
        overflow: "none",
        margin: 14,
      },
      inverse: true,
    },
    series: [
      {
        type: "bar",
        data: data.map((item) => {
          const { label, color, ...rest } = item;
          return {
            ...rest,
            value: item.value,
            itemStyle: {
              color: color ?? colors.chart_blue_light,
              borderRadius: [0, 0, 0, 0],
            },
          };
        }),
        barWidth: "50%",
        barGap: "5%",
        barCategoryGap: "5%",
        emphasis: {
          itemStyle: {
            opacity: 0.8,
          },
        },
      },
    ],
  };
};

const createVerticalBarOption = (
  data: any[],
  tooltipOpts?: BarChartTooltipOptions,
) => {
  const rawValues = data.map((item) => Number(item.value));
  const useLogAxis = shouldUseLogAxisForBarValues(rawValues);
  return {
    tooltip: {
      ...axisTooltipBase,
      textStyle: {
        color: colors.chart_text,
        fontSize: "0.75rem",
        fontWeight: 400,
      },
      formatter: (params: unknown) =>
        formatAxisBarTooltip(params, tooltipOpts, colors.chart_blue),
    },
    grid: {
      left: "-20px",
      right: "0px",
      top: "2%",
      bottom: "-2%",
      containLabel: false,
    },
    xAxis: {
      type: "category",
      data: data.map((item) => item.label),
      axisLine: {
        show: false,
      },
      axisTick: {
        show: false,
      },
      axisLabel: {
        show: true,
        fontSize: "0.6875rem",
        fontWeight: 500,
        color: colors.black,
        fontFamily: "Inter",
      },
    },
    yAxis: {
      type: useLogAxis ? "log" : "value",
      ...(useLogAxis ? { logBase: 10 } : {}),
      min: useLogAxis ? undefined : 0,
      show: false,
      splitLine: {
        show: false,
      },
    },
    series: [
      {
        type: "bar",
        data: data.map((item) => {
          const { label, color, ...rest } = item;
          return {
            ...rest,
            value: item.value,
            itemStyle: {
              color: color ?? colors.chart_blue,
              borderRadius: [2, 2, 0, 0],
            },
          };
        }),
        barWidth: "50%",
        emphasis: {
          itemStyle: {
            opacity: 0.8,
          },
        },
      },
    ],
  };
};

const MiniEChart: React.FC<{
  option: any;
  height?: number;
}> = ({ option, height = 108 }) => {
  const chartRef = useRef<HTMLDivElement>(null);
  const chartInstance = useRef<echarts.ECharts | null>(null);

  useEffect(() => {
    if (chartRef.current) {
      if (chartInstance.current) {
        chartInstance.current.dispose();
      }

      chartInstance.current = echarts.init(chartRef.current);
      chartInstance.current.setOption(option);

      const resizeHandler = () => {
        if (chartInstance.current) {
          chartInstance.current.resize();
        }
      };

      const observer = new ResizeObserver(resizeHandler);
      observer.observe(chartRef.current);

      return () => {
        observer.disconnect();
        if (chartInstance.current) {
          chartInstance.current.dispose();
        }
      };
    }
  }, [option]);

  return (
    <Box
      ref={chartRef}
      sx={{
        ...operationsDashboardStyles.miniChart,
        height: height,
      }}
    />
  );
};

export const ChartCard: React.FC<ChartCardProps> = ({ data }) => {
  const renderChart = () => {
    if (!data.chart?.data?.length) {
      return (
        <Box sx={operationsDashboardStyles.noChartData}>
          <Typography sx={operationsDashboardStyles.cardSubtitle}>
            No data available
          </Typography>
        </Box>
      );
    }

    const {
      type,
      variant,
      data: chartData,
      tooltipValuePrefix,
      tooltipValueLabel,
      tooltipSecondaryValueKey,
      tooltipSecondaryValueLabel,
      tooltipSecondaryValuePrefix,
    } = data.chart;

    if (type === "bar") {
      const tooltipOpts = {
        tooltipValuePrefix,
        tooltipValueLabel,
        tooltipSecondaryValueKey,
        tooltipSecondaryValueLabel,
        tooltipSecondaryValuePrefix,
      };
      const option =
        variant === "horizontal"
          ? createHorizontalBarOption(chartData, tooltipOpts)
          : createVerticalBarOption(chartData, tooltipOpts);

      return <MiniEChart option={option} height={108} />;
    }

    return (
      <Box sx={operationsDashboardStyles.defaultChartType}>
        <Typography variant="caption">{type.toUpperCase()} Chart</Typography>
      </Box>
    );
  };

  const hasValue = data.value !== undefined && data.value !== "";

  return (
    <BaseCard data={data}>
      {hasValue && (
        <Box sx={operationsDashboardStyles.flexCenter}>
          <Typography sx={operationsDashboardStyles.unitText}>
            {data.unit ?? ""}
          </Typography>
          <Typography sx={operationsDashboardStyles.valueText}>
            {data.value}
          </Typography>
        </Box>
      )}
      {hasValue && <Divider sx={operationsDashboardStyles.chartDivider} />}
      <Box sx={operationsDashboardStyles.flexColumn}>{renderChart()}</Box>
    </BaseCard>
  );
};
